import { c as _c } from "react/compiler-runtime";
import React from "react";
import { InlineRichText } from "@/components/atoms/RichText";
import type { PortableTextBlock } from "@portabletext/types";
import * as LucideIcons from "lucide-react";
import { SECTION_CONTAINER_WIDTHS, type SectionContainerWidth } from "@/components/atoms/section-constants";
import { SectionBackground } from "../modules/Section/SectionBackground";
import { SectionContent } from "../modules/Section/SectionContent";
import { BackgroundPatternType } from "@/components/atoms/BackgroundPattern";
import { isDarkColor } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { getPaddingClass } from "@/lib/spacing";
import { ContentImage, ContentVideo } from "@/content/types";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
interface Definition {
  _key?: string;
  term: string;
  abbreviation?: string;
  definition?: PortableTextBlock[];
  icon?: string;
}
interface SectionStyle {
  backgroundColor?: string;
  backgroundImage?: ContentImage;
  backgroundVideo?: ContentVideo;
  backgroundPattern?: string;
  patternOpacity?: number;
  textColor?: string;
  paddingTop?: string;
  paddingBottom?: string;
  containerWidth?: string;
  customClasses?: string;
}
interface PortableGlossaryData {
  _type: "portableGlossary";
  _key?: string;
  heading?: string;
  definitions?: Definition[];
  layout?: "single" | "two-column";
  showNumbers?: boolean;
  style?: SectionStyle;
}
interface PortableGlossaryProps {
  value: PortableGlossaryData;
}

/**
 * Single Definition Card Component
 */
function DefinitionCard(t0) {
  const $ = _c(21);
  const {
    definition,
    index,
    showNumber
  } = t0;
  const IconComponent = definition.icon ? (LucideIcons as unknown as Record<string, React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>>)[definition.icon] : null;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("group relative", "rounded-2xl p-6 md:p-8", "bg-white/5 border border-white/10", "backdrop-blur-sm shadow-lg", "transition-all duration-300 hover:bg-white/10");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== index || $[2] !== showNumber) {
    t2 = showNumber && <Text className="opacity-50 font-medium tabular-nums">{index + 1}.</Text>;
    $[1] = index;
    $[2] = showNumber;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== definition.abbreviation) {
    t3 = definition.abbreviation && <Text className="text-primary font-medium">({definition.abbreviation})</Text>;
    $[4] = definition.abbreviation;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== IconComponent) {
    t4 = IconComponent && <IconComponent className="w-6 h-6 text-primary opacity-70 self-center ml-2" strokeWidth={2} />;
    $[6] = IconComponent;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== definition.term || $[9] !== t3 || $[10] !== t4) {
    t5 = <Text className="flex items-baseline gap-2">{definition.term}{t3}{t4}</Text>;
    $[8] = definition.term;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t5) {
    t6 = <Text as="p" className="text-xl md:text-2xl font-bold tracking-tight flex items-baseline gap-3 mb-4">{t2}{t5}</Text>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== definition.definition || $[16] !== showNumber) {
    t7 = definition.definition && definition.definition.length > 0 && <Box className={cn("prose prose-lg max-w-none opacity-80", showNumber ? "pl-11" : "pl-0")}><InlineRichText content={definition.definition} /></Box>;
    $[15] = definition.definition;
    $[16] = showNumber;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <Box className={t1}>{t6}{t7}</Box>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

/**
 * PortableGlossary Component
 *
 * A block for displaying key terminology with definitions.
 * Supports single and two-column layouts with optional numbered list.
 */
const PortableGlossary = t0 => {
  const $ = _c(52);
  const {
    value
  } = t0;
  const {
    heading,
    definitions,
    layout: t1,
    showNumbers: t2,
    style
  } = value;
  const layout = t1 === undefined ? "two-column" : t1;
  const showNumbers = t2 === undefined ? true : t2;
  const bgColor = style?.backgroundColor || "aq-blue";
  const paddingTop = style?.paddingTop || "pt-16";
  const paddingBottom = style?.paddingBottom || "pb-16";
  const containerWidthValue = style?.containerWidth || "max-w-7xl";
  const customClasses = style?.customClasses || "";
  let T0;
  let T1;
  let containerSize;
  let gridClasses;
  let isDark;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== bgColor || $[1] !== containerWidthValue || $[2] !== customClasses || $[3] !== definitions || $[4] !== heading || $[5] !== layout || $[6] !== paddingBottom || $[7] !== paddingTop) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const ptClass = getPaddingClass(paddingTop, "pt-16");
      const pbClass = getPaddingClass(paddingBottom, "pb-16");
      containerSize = (containerWidthValue in SECTION_CONTAINER_WIDTHS ? containerWidthValue : "max-w-7xl") as SectionContainerWidth;
      let t8;
      if ($[18] !== bgColor) {
        t8 = isDarkColor(bgColor);
        $[18] = bgColor;
        $[19] = t8;
      } else {
        t8 = $[19];
      }
      isDark = t8;
      if (!definitions || definitions.length === 0) {
        t7 = null;
        break bb0;
      }
      gridClasses = layout === "two-column" ? "grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" : "flex flex-col gap-6";
      T1 = Box;
      t6 = "not-prose my-16";
      T0 = SectionBackground;
      t3 = "section";
      t4 = heading || "Glossary";
      t5 = cn("glossary-block", "rounded-2xl md:rounded-3xl border border-white/10 shadow-2xl", ptClass, pbClass, customClasses);
    }
    $[0] = bgColor;
    $[1] = containerWidthValue;
    $[2] = customClasses;
    $[3] = definitions;
    $[4] = heading;
    $[5] = layout;
    $[6] = paddingBottom;
    $[7] = paddingTop;
    $[8] = T0;
    $[9] = T1;
    $[10] = containerSize;
    $[11] = gridClasses;
    $[12] = isDark;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    T0 = $[8];
    T1 = $[9];
    containerSize = $[10];
    gridClasses = $[11];
    isDark = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  const t8 = style?.backgroundImage;
  const t9 = style?.backgroundVideo;
  const t10 = style?.backgroundPattern as BackgroundPatternType;
  const t11 = style?.patternOpacity;
  let t12;
  if ($[20] !== heading) {
    t12 = heading && <Text as="p" className="text-2xl md:text-3xl font-bold tracking-tight mb-10 opacity-90">{heading}</Text>;
    $[20] = heading;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== definitions || $[23] !== showNumbers) {
    let t14;
    if ($[25] !== showNumbers) {
      t14 = (def, index) => <DefinitionCard key={def._key || `def-${index}`} definition={def as Definition} index={index} showNumber={showNumbers ?? true} />;
      $[25] = showNumbers;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    t13 = definitions.map(t14);
    $[22] = definitions;
    $[23] = showNumbers;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[27] !== gridClasses || $[28] !== t13) {
    t14 = <Box className={gridClasses}>{t13}</Box>;
    $[27] = gridClasses;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t14) {
    t15 = <Box className="relative z-10">{t12}{t14}</Box>;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== containerSize || $[34] !== t15) {
    t16 = <SectionContent size={containerSize}>{t15}</SectionContent>;
    $[33] = containerSize;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== T0 || $[37] !== bgColor || $[38] !== isDark || $[39] !== t10 || $[40] !== t11 || $[41] !== t16 || $[42] !== t3 || $[43] !== t4 || $[44] !== t5 || $[45] !== t8 || $[46] !== t9) {
    t17 = <T0 as={t3} aria-label={t4} className={t5} backgroundColor={bgColor} backgroundImage={t8} backgroundVideo={t9} backgroundPattern={t10} patternOpacity={t11} isDark={isDark}>{t16}</T0>;
    $[36] = T0;
    $[37] = bgColor;
    $[38] = isDark;
    $[39] = t10;
    $[40] = t11;
    $[41] = t16;
    $[42] = t3;
    $[43] = t4;
    $[44] = t5;
    $[45] = t8;
    $[46] = t9;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== T1 || $[49] !== t17 || $[50] !== t6) {
    t18 = <T1 className={t6}>{t17}</T1>;
    $[48] = T1;
    $[49] = t17;
    $[50] = t6;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  return t18;
};
export default PortableGlossary;